import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OcrDebtorGroup } from 'src/app/services/form/form.service';
import { PayeePopupComponent } from '../../../shared/payee-popup/payee-popup.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

export type CurrentCaseClaimDisplay = {
  fileName: string;
  amountLabel: string;
  dueDate: string;
};

export type CurrentCaseDisplay = {
  debtor_name: string | null;
  debtor_tax_number: string | null;
  claims: CurrentCaseClaimDisplay[];
};

export type MultiUploadResultDialogData = {
  skippedFileNames: string[];
  debtorGroups: OcrDebtorGroup[];
  canKeepExistingDebtor: boolean;
  showCurrentCaseOption: boolean;
  currentCase: CurrentCaseDisplay;
};

export type MultiUploadResultDialogResult =
  | { action: 'add_all_claims' }
  | { action: 'apply_current_debtor' }
  | { action: 'replace_debtor'; selectedGroup: OcrDebtorGroup };

@Component({
  selector: 'app-multi-upload-result-dialog',
  templateUrl: './multi-upload-result-dialog.component.html',
  styleUrls: ['./multi-upload-result-dialog.component.scss'],
  imports: [PayeePopupComponent, PayeeButtonComponent, TranslateModule],
})
export class MultiUploadResultDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<MultiUploadResultDialogComponent, MultiUploadResultDialogResult>>(MatDialogRef);
  data = inject<MultiUploadResultDialogData>(MAT_DIALOG_DATA);

  selectedDebtorGroupIndex: number | null = null;

  ngOnInit(): void {
    this.selectedDebtorGroupIndex = 0;
  }

  get showCurrentCaseOption(): boolean {
    return this.data.showCurrentCaseOption;
  }

  get applyLabelKey(): string {
    if (this.isCurrentDebtorSelected) {
      return 'MULTI_UPLOAD_RESULT_DIALOG.APPLY_KEEP_DEBTOR';
    }

    return this.data.canKeepExistingDebtor
      ? 'MULTI_UPLOAD_RESULT_DIALOG.APPLY_REPLACE_DEBTOR'
      : 'MULTI_UPLOAD_RESULT_DIALOG.APPLY';
  }

  get isCurrentDebtorSelected(): boolean {
    return this.showCurrentCaseOption && this.selectedDebtorGroupIndex === 0;
  }

  get canAddAllClaims(): boolean {
    return this.data.debtorGroups.some((group) => group.parsedInvoices.length > 0);
  }

  get addAllClaimsLabelKey(): string {
    return this.data.canKeepExistingDebtor
      ? 'MULTI_UPLOAD_RESULT_DIALOG.KEEP_DEBTOR_ADD_CLAIMS'
      : 'MULTI_UPLOAD_RESULT_DIALOG.ADD_ALL_CLAIMS';
  }

  selectDebtorGroup(index: number): void {
    this.selectedDebtorGroupIndex = index;
  }

  getDebtorGroupOptionIndex(groupIndex: number): number {
    return groupIndex + (this.showCurrentCaseOption ? 1 : 0);
  }

  apply(): void {
    if (this.isCurrentDebtorSelected) {
      this.dialogRef.close({ action: 'apply_current_debtor' });
      return;
    }

    const selectedGroup = this.getSelectedDebtorGroup();
    if (!selectedGroup) {
      return;
    }

    this.dialogRef.close({
      action: 'replace_debtor',
      selectedGroup,
    });
  }

  addAllClaims(): void {
    this.dialogRef.close({ action: 'add_all_claims' });
  }

  private getSelectedDebtorGroup(): OcrDebtorGroup | undefined {
    if (this.selectedDebtorGroupIndex === null) {
      return undefined;
    }

    const groupIndex = this.showCurrentCaseOption
      ? this.selectedDebtorGroupIndex - 1
      : this.selectedDebtorGroupIndex;

    if (groupIndex < 0) {
      return undefined;
    }

    return this.data.debtorGroups[groupIndex];
  }
}
